/**
 * Wave 3 · 22 #9 — LPO drafts made from site requests, and their approval:
 *  - `LpoDraftsList`: each draft with its status (Draft / Pending approval / Approved), supplier, value, how many
 *    requests it came from and its age, and the one next step for this person (Submit for approval · Approve ·
 *    Send to supplier); ⋯ = Edit draft, Return to draft…, Discard draft….
 *  - `ApprovalLimitNote`: "Drafts over AED 50,000 go to management for approval" (+ Change for Admins).
 *  - `useDraftActions`: the API calls behind those buttons (the server enforces who may approve and that a draft
 *    above the limit is approved before it is sent). Every change is recorded server-side, so the shell's toast
 *    offers Undo.
 */
import { useCallback, useEffect, useState } from 'react';
import { BadgeCheck, CircleCheck, FilePlus, Info, Pencil, Send, ShieldCheck, Trash2, Undo2 } from 'lucide-react';
import api from '@/lib/api';
import { getErrorMessage } from '@/lib/errors';
import { formatDate, formatMoney, formatNumber, formatTimeAgo, toDateInputValue } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RowActions } from '@/components/ui/row-actions';
import { StatusPill } from '@/components/ui/status-pill';
import { Textarea } from '@/components/ui/textarea';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { notify } from '@/components/ui/notify';
import { LinkButton, MissingText, plural } from './SfUi';
import { DEFAULT_APPROVAL_THRESHOLD, draftRequestCount, draftStage } from './draftsModel';

const aed0 = (value) => formatMoney(value, { decimals: 0 });

/** GET/PUT /procurement/approval-settings — the approval limit (default AED 50,000 until it loads). */
export function useApprovalSettings(enabled = true) {
  const [settings, setSettings] = useState({ threshold: DEFAULT_APPROVAL_THRESHOLD, can_change: false, can_approve: false, loaded: false });
  const load = useCallback(async () => {
    try {
      const res = await api.get('/procurement/approval-settings');
      setSettings({ ...res.data, loaded: true });
    } catch {
      setSettings((prev) => ({ ...prev, loaded: true }));
    }
  }, []);
  useEffect(() => { if (enabled) load(); }, [enabled, load]);
  const save = useCallback(async (threshold) => {
    const res = await api.put('/procurement/approval-settings', { threshold });
    setSettings({ ...res.data, loaded: true });
    return res.data;
  }, []);
  return { ...settings, reload: load, save };
}

function SendDialog({ draft, open, onOpenChange, onSent }) {
  const today = toDateInputValue(new Date());
  const [expected, setExpected] = useState('');
  const [sentOn, setSentOn] = useState(today);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setExpected(''); setSentOn(today); setError(''); setBusy(false); } }, [open, today]);
  const submit = async (event) => {
    event.preventDefault();
    if (!expected) { setError('Pick the delivery date the supplier promised'); return; }
    setBusy(true);
    setError('');
    try {
      const res = await api.post(`/lpos/${draft.id}/send`, { expected_start_delivery_date: expected, date_sent: sentOn || today });
      onOpenChange(false);
      onSent?.(res.data);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not send the LPO'));
      setBusy(false);
    }
  };
  if (!draft) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[420px] rounded-card border-line bg-surface text-ink" data-testid="lpo-send-dialog">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle className="font-heading text-[17px] font-bold">Send {draft.lpo_number} to {draft.supplier || 'the supplier'}</DialogTitle>
            <DialogDescription className="text-[13px] text-ink-3">
              It becomes an open order: its lines count as incoming in Forecast and its requests read “Ordered”.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="send-expected" className="text-[12px] font-semibold text-ink-2">Expected delivery</Label>
              <Input id="send-expected" type="date" min={today} value={expected} onChange={(e) => setExpected(e.target.value)} className="h-9" data-testid="lpo-send-expected" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="send-on" className="text-[12px] font-semibold text-ink-2">Sent on</Label>
              <Input id="send-on" type="date" max={today} value={sentOn} onChange={(e) => setSentOn(e.target.value)} className="h-9" />
            </div>
          </div>
          {error ? <p className="text-[12.5px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="lpo-send-submit"><Send aria-hidden="true" />{busy ? 'Sending…' : 'Mark as sent'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReturnDialog({ draft, open, onOpenChange, onReturned }) {
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setNote(''); setError(''); setBusy(false); } }, [open]);
  const submit = async (event) => {
    event.preventDefault();
    if (!note.trim()) { setError('Say why it goes back — the buyer sees this'); return; }
    setBusy(true);
    try {
      const res = await api.post(`/lpos/${draft.id}/return`, { note: note.trim() });
      onOpenChange(false);
      onReturned?.(res.data);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not return the draft'));
      setBusy(false);
    }
  };
  if (!draft) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[420px] rounded-card border-line bg-surface text-ink" data-testid="lpo-return-dialog">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle className="font-heading text-[17px] font-bold">Return {draft.lpo_number} to draft?</DialogTitle>
            <DialogDescription className="text-[13px] text-ink-3">{draft.supplier || 'Supplier missing'} · {aed0(draft.approval_amount ?? draft.total_value)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="return-note" className="text-[12px] font-semibold text-ink-2">Reason</Label>
            <Textarea id="return-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. ask Siemens for a second quote" data-testid="lpo-return-note" />
          </div>
          {error ? <p className="text-[12.5px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" variant="outline" disabled={busy} data-testid="lpo-return-submit"><Undo2 aria-hidden="true" />{busy ? 'Returning…' : 'Return to draft'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** The calls behind the draft buttons; `onChanged` reloads the page data. Returns [run(action, draft), dialogs]. */
export function useDraftActions(onChanged) {
  const confirm = useConfirm();
  const [sending, setSending] = useState(null);
  const [returning, setReturning] = useState(null);

  const run = useCallback(async (action, draft) => {
    const number = draft.lpo_number || 'the draft';
    try {
      if (action === 'submit') {
        const res = await api.post(`/lpos/${draft.id}/submit`);
        if (res.data?.status === 'Pending approval') {
          notify.success(`${number} sent for approval`, { description: `${aed0(res.data.approval_amount)} · management has been notified` });
        } else {
          notify.success(`${number} is ready to send`, { description: 'Within the approval limit — no approval needed' });
        }
        await onChanged?.();
      } else if (action === 'approve') {
        const ok = await confirm({
          title: `Approve ${number}?`,
          subtitle: `${draft.supplier || 'Supplier missing'} · ${formatMoney(draft.approval_amount ?? draft.total_value)} · ${plural((draft.line_items || []).length, 'line')}`,
          description: 'Procurement can then send it to the supplier.',
          confirmLabel: 'Approve',
          icon: BadgeCheck,
          onConfirm: async () => { await api.post(`/lpos/${draft.id}/approve`, {}); },
        });
        if (!ok) return;
        notify.success(`${number} approved`);
        await onChanged?.();
      } else if (action === 'send') {
        setSending(draft);
      } else if (action === 'return') {
        setReturning(draft);
      } else if (action === 'discard') {
        const requests = draftRequestCount(draft);
        const ok = await confirm({
          title: `Discard draft ${number}?`,
          subtitle: `${draft.supplier || 'Supplier missing'} · ${formatMoney(draft.approval_amount ?? draft.total_value)}`,
          description: requests ? `Its ${plural(requests, 'site request')} go back to the queue and can be put on another draft.` : 'The draft is cancelled.',
          tone: 'danger',
          confirmLabel: 'Discard draft',
          cancelLabel: 'Keep draft',
          onConfirm: async () => { await api.put(`/lpos/${draft.id}`, { status: 'Cancelled' }); },
        });
        if (!ok) return;
        notify.success(`Draft ${number} discarded`);
        await onChanged?.();
      }
    } catch (err) {
      notify.error(`Could not update ${number}`, { description: getErrorMessage(err) });
      await onChanged?.();
    }
  }, [confirm, onChanged]);

  const dialogs = (
    <>
      <SendDialog
        draft={sending}
        open={Boolean(sending)}
        onOpenChange={(open) => { if (!open) setSending(null); }}
        onSent={(lpo) => { notify.success(`${lpo.lpo_number} sent to ${lpo.supplier}`, { description: `Expected ${formatDate(lpo.expected_start_delivery_date)}` }); onChanged?.(); }}
      />
      <ReturnDialog
        draft={returning}
        open={Boolean(returning)}
        onOpenChange={(open) => { if (!open) setReturning(null); }}
        onReturned={(lpo) => { notify.success(`${lpo.lpo_number} returned to draft`); onChanged?.(); }}
      />
    </>
  );
  return [run, dialogs];
}

const PRIMARY_ICON = { submit: ShieldCheck, approve: BadgeCheck, send: Send, price: Pencil };

/** One draft. `compact` = the Site requests card (one line + the next step); full = the Orders tab card. */
function DraftRow({ draft, threshold, canApprove, canEdit, onAction, onEdit, compact, highlighted }) {
  const stage = draftStage(draft, { threshold, canApprove, canEdit });
  const requests = draftRequestCount(draft);
  const Icon = stage.primary ? PRIMARY_ICON[stage.primary.key] : null;
  const meta = [
    draft.supplier || null,
    draft.project_name || null,
    aed0(stage.amount),
    requests ? `from ${plural(requests, 'request')}` : null,
    draft.created_at ? formatTimeAgo(draft.created_at) : null,
  ].filter(Boolean);
  const primaryButton = stage.primary ? (
    <Button size="xs" variant={stage.primary.key === 'approve' ? 'default' : 'outline'} className="shrink-0"
      onClick={() => (stage.primary.key === 'price' ? onEdit(draft) : onAction(stage.primary.key, draft))} data-testid={`lpo-draft-${stage.primary.key}-${draft.id}`}>
      {Icon ? <Icon aria-hidden="true" /> : null}{stage.primary.label}
    </Button>
  ) : null;
  const menu = (
    <RowActions
      size="sm"
      label={`Actions for ${draft.lpo_number}`}
      data-testid={`lpo-draft-menu-${draft.id}`}
      items={[
        canEdit && { label: 'Edit draft', icon: Pencil, onSelect: () => onEdit(draft), testId: `lpo-draft-edit-${draft.id}` },
        stage.canReturn && { label: 'Return to draft…', icon: Undo2, onSelect: () => onAction('return', draft), testId: `lpo-draft-return-${draft.id}` },
        canEdit && { label: 'Discard draft…', icon: Trash2, danger: true, onSelect: () => onAction('discard', draft), testId: `lpo-draft-discard-${draft.id}` },
      ]}
    />
  );
  const notes = (
    <>
      {stage.waiting ? <p className="text-[12px] font-semibold text-status-amber-ink">{stage.waiting}</p> : null}
      {stage.approvedBy ? <p className="flex items-center gap-1 text-[12px] text-ink-3"><CircleCheck className="h-3 w-3 text-status-green-ink" aria-hidden="true" />Approved by {stage.approvedBy}</p> : null}
      {stage.returned ? <p className="text-[12px] text-status-amber-ink">Returned: “{stage.returned}”</p> : null}
      {stage.changed ? <p className="text-[12px] text-status-amber-ink" data-testid={`lpo-draft-changed-${draft.id}`}>{stage.changed}</p> : null}
      {stage.unpriced ? <p className="text-[12px] text-status-amber-ink" data-testid={`lpo-draft-unpriced-${draft.id}`}>{plural(stage.unpriced, 'line')} without a unit price — price {stage.unpriced === 1 ? 'it' : 'them'} before it is submitted or sent</p> : null}
    </>
  );
  return (
    <li
      className={cn(
        'rounded-xl border border-dashed border-[color:var(--line-dashed)] bg-surface px-3 py-2.5',
        highlighted && 'border-solid border-brand shadow-[0_0_0_3px_var(--brand-soft)]'
      )}
      data-testid={`lpo-draft-${draft.id}`}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1.5">
        <span className="flex min-w-0 items-center gap-2">
          <FilePlus className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden="true" />
          <span className="whitespace-nowrap font-mono text-[13px] font-semibold text-ink" data-testid={`lpo-draft-number-${draft.id}`}>{draft.lpo_number}</span>
          <StatusPill status={stage.status} tone={stage.tone} size="sm" className="shrink-0" />
          {!draft.supplier ? <MissingText className="truncate">Supplier missing</MissingText> : null}
        </span>
        <span className="ml-auto flex shrink-0 items-center gap-1">
          {compact ? null : primaryButton}
          {menu}
        </span>
      </div>
      <div className="pl-[22px]">
        <p className="mt-0.5 truncate text-[12px] text-ink-3" title={meta.join(' · ')}>{meta.join(' · ')}</p>
        {compact ? (
          <div className="mt-1.5 flex min-w-0 items-center justify-between gap-2">
            <div className="min-w-0">{notes}</div>
            {primaryButton}
          </div>
        ) : notes}
        {!compact ? (
          <ul className="mt-1.5 space-y-0.5 text-[12px] text-ink-2">
            {(draft.line_items || []).slice(0, 4).map((line) => (
              <li key={line.id || line.part_number} className="flex min-w-0 gap-2">
                <span className="shrink-0 whitespace-nowrap font-mono text-ink">{line.part_number}</span>
                <span className="min-w-0 truncate text-ink-3">{line.description}</span>
                <span className="ml-auto shrink-0 tabular-nums">{formatNumber(line.ordered_qty)} × {Number(line.unit_price) > 0 ? formatNumber(line.unit_price, { decimals: 2 }) : '—'}</span>
              </li>
            ))}
            {(draft.line_items || []).length > 4 ? <li className="text-ink-3">+ {plural(draft.line_items.length - 4, 'more line')}</li> : null}
          </ul>
        ) : null}
      </div>
    </li>
  );
}

export function LpoDraftsList({ drafts, threshold, canApprove, canEdit, onAction, onEdit, compact = false, highlightId, className, testId = 'lpo-drafts' }) {
  if (!drafts.length) return null;
  return (
    <ul className={cn('space-y-2', className)} data-testid={testId}>
      {drafts.map((draft) => (
        <DraftRow key={draft.id} draft={draft} threshold={threshold} canApprove={canApprove} canEdit={canEdit}
          onAction={onAction} onEdit={onEdit} compact={compact} highlighted={draft.id === highlightId} />
      ))}
    </ul>
  );
}

/** "Drafts over AED 50,000 go to management for approval" (+ Change for Admins / Super Admins). */
export function ApprovalLimitNote({ settings, className, onSaved }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setValue(String(settings.threshold ?? '')); setError(''); } }, [open, settings.threshold]);
  const save = async (event) => {
    event.preventDefault();
    const number = Number(String(value).replace(/,/g, ''));
    if (!Number.isFinite(number) || number < 0) { setError('Enter an amount of 0 or more'); return; }
    setBusy(true);
    try {
      const saved = await settings.save(number);
      // Drafts marked ready under the old limit that are above the new one go back to Draft (the server does it).
      const back = Array.isArray(saved?.returned_drafts) ? saved.returned_drafts : [];
      notify.success(`Approval limit set to ${aed0(number)}`, back.length
        ? { description: `${back.join(', ')} ${back.length === 1 ? 'goes' : 'go'} back to draft — submit ${back.length === 1 ? 'it' : 'them'} for approval` }
        : {});
      setOpen(false);
      if (back.length) await onSaved?.();
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save the limit'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={cn('flex items-start gap-1.5 text-[12px] leading-snug text-ink-3', className)} data-testid="lpo-approval-limit">
      <ShieldCheck className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="min-w-0">
      Drafts over {aed0(settings.threshold)} go to management for approval
      {settings.can_change ? <span className="text-ink-4" aria-hidden="true"> · </span> : null}
      {settings.can_change ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild><LinkButton className="text-[12px]" data-testid="lpo-approval-limit-change">Change</LinkButton></PopoverTrigger>
          <PopoverContent align="start" className="w-[260px] rounded-xl border-line bg-surface p-3.5 text-ink shadow-overlay">
            <form onSubmit={save} className="space-y-2.5">
              <Label htmlFor="approval-limit" className="text-[12px] font-semibold text-ink-2">Approval limit (AED)</Label>
              <Input id="approval-limit" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} className="h-9 text-right tabular-nums" />
              <p className="flex items-start gap-1.5 text-[11.5px] text-ink-3"><Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />Drafts above it need an Admin or Super Admin to approve them before they are sent.</p>
              {error ? <p className="text-[12px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" size="sm" disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
              </div>
            </form>
          </PopoverContent>
        </Popover>
      ) : null}
      </span>
    </div>
  );
}
